import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const { clearCart } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            cleared.current = true;
            clearCart();
          }
          return;
        }
      } catch {
        /* keep polling */
      }
      tries += 1;
      if (tries < 12) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId, clearCart]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="text-center max-w-md"
      >
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-[#E05A36] animate-spin mx-auto mb-6" />
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight mb-3">Confirming payment</h1>
            <p className="text-sm text-[#B8AEA5]" data-testid="payment-status-message">Hold tight — we're talking to Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-[#7BC47F] mx-auto mb-6" />
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight mb-3" data-testid="payment-confirmed-heading">
              Order locked in
            </h1>
            <p className="text-sm text-[#B8AEA5] mb-8" data-testid="payment-status-message">
              Payment confirmed. A confirmation email is on its way. Your gear ships soon — welcome to the rebellion.
            </p>
            <Link to="/shop" className="inline-block bg-[#E05A36] text-[#0C0A09] font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#F06B47] transition-colors" data-testid="payment-continue-shopping">
              Keep shopping
            </Link>
          </>
        )}
        {(status === "timeout" || status === "failed") && (
          <>
            <Loader2 className="w-12 h-12 text-[#E29D47] mx-auto mb-6" />
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight mb-3">Still processing</h1>
            <p className="text-sm text-[#B8AEA5] mb-8" data-testid="payment-status-message">
              Your payment is taking longer than usual to confirm. If you were charged, your order is safe — check your email.
            </p>
            <Link to="/" className="inline-block border border-[#2C2623] hover:border-[#E05A36] hover:text-[#E05A36] font-bold uppercase tracking-wider px-8 py-4 transition-colors" data-testid="payment-home-link">
              Back home
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
};

export const PaymentCancel = () => (
  <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="payment-cancel-page">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
      className="text-center max-w-md"
    >
      <XCircle className="w-12 h-12 text-[#E05A36] mx-auto mb-6" />
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight mb-3">Checkout cancelled</h1>
      <p className="text-sm text-[#B8AEA5] mb-8">No charge was made. Your cart is still waiting for you.</p>
      <Link to="/shop" className="inline-block bg-[#E05A36] text-[#0C0A09] font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#F06B47] transition-colors" data-testid="cancel-back-to-shop">
        Back to the store
      </Link>
    </motion.div>
  </div>
);
